import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getNotifications, logoutUser, searchGeneral, setNotificationRead } from "../api";
import "../assets/css/topbar.css";

function withFocusRefresh(path) {
  if (!path) return path;
  const [base, hash = ""] = String(path).split("#");
  const [pathname, search = ""] = base.split("?");
  const params = new URLSearchParams(search);
  if (params.has("focusId")) params.set("focusTick", `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const nextSearch = params.toString();
  return `${pathname}${nextSearch ? `?${nextSearch}` : ""}${hash ? `#${hash}` : ""}`;
}

export default function Topbar({ companyName, companyCode, companyLogoUrl, onAnyAction }) {
  const [accountOpen, setAccountOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchSections, setSearchSections] = useState([]);
  const [searchError, setSearchError] = useState("");
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [notificationsUnread, setNotificationsUnread] = useState(0);
  const accountMenuRef = useRef(null);
  const notificationsMenuRef = useRef(null);
  const searchWrapRef = useRef(null);
  const navigate = useNavigate();
  let user = null;

  try {
    user = JSON.parse(localStorage.getItem("user") || "null");
  } catch {}

  const accountName = user?.fullName || user?.name || user?.username || "Utente";
  const accountRole = user?.roleLabel || user?.role || user?.profile || "Ruolo non definito";
  const totalSearchResults = useMemo(() => searchSections.reduce((sum, section) => sum + (section.items?.length || 0), 0), [searchSections]);

  const formatNotificationDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
  };

  const notificationTypeLabel = (notification) => {
    const category = String(notification?.category || notification?.scope || "").trim().toUpperCase();
    const labels = { CHECKIN_DATA: "Check-in", ARRIVAL: "Arrivi", CHECKOUT: "Check-out", ACCOUNT: "Soggiorni", ROOM_STATUS: "Camere", MAINTENANCE: "Manutenzioni", QUOTE: "Preventivi", FISCAL_DOCUMENT: "Documenti fiscali", HOTEL: "Hotel" };
    if (!category) return "Hotel";
    return labels[category] || category.charAt(0) + category.slice(1).toLowerCase().replaceAll("_", " ");
  };

  const getNotificationErrorMessage = (error) => {
    const status = error?.response?.status;
    if (status === 401) return "Sessione scaduta: effettua nuovamente il login";
    if (status === 403) return "Notifiche non abilitate per questo utente";
    if (status === 404) return "API notifiche non trovata: aggiorna e riavvia il backend";
    if (status >= 500) return "Errore server notifiche: controlla il terminale backend";
    return "Notifiche non disponibili";
  };

  useEffect(() => {
    const closeMenusOnOutsideClick = (event) => {
      const clickedAccount = accountMenuRef.current?.contains(event.target);
      const clickedNotifications = notificationsMenuRef.current?.contains(event.target);
      const clickedSearch = searchWrapRef.current?.contains(event.target);

      if (!clickedAccount) {
        setAccountOpen(false);
      }

      if (!clickedNotifications) {
        setNotificationsOpen(false);
      }

      if (!clickedSearch) {
        setSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", closeMenusOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeMenusOnOutsideClick);
  }, []);

  useEffect(() => {
    const q = searchText.trim();
    setSearchError("");

    if (q.length < 2) {
      setSearchSections([]);
      setSearchLoading(false);
      return undefined;
    }

    let active = true;
    setSearchLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const data = await searchGeneral({ q });
        if (!active) return;
        setSearchSections(Array.isArray(data?.sections) ? data.sections : []);
      } catch {
        if (!active) return;
        setSearchSections([]);
        setSearchError("Ricerca non disponibile");
      } finally {
        if (active) setSearchLoading(false);
      }
    }, 220);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [searchText]);

  const loadNotificationsPreview = useCallback(async ({ showLoading = false } = {}) => {
    if (showLoading) setNotificationsLoading(true);
    setNotificationsError("");
    try {
      const data = await getNotifications({ limit: 5, skip: 0 });
      setNotifications(Array.isArray(data?.items) ? data.items : []);
      setNotificationsUnread(Number(data?.stats?.unread || 0));
    } catch (error) {
      setNotifications([]);
      setNotificationsUnread(0);
      setNotificationsError(getNotificationErrorMessage(error));
    } finally {
      if (showLoading) setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotificationsPreview();
    const timer = window.setInterval(() => loadNotificationsPreview(), 60000);
    const handleNotificationsChanged = () => loadNotificationsPreview();
    window.addEventListener("vesta:notifications-changed", handleNotificationsChanged);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("vesta:notifications-changed", handleNotificationsChanged);
    };
  }, [loadNotificationsPreview]);

  useEffect(() => {
    if (!notificationsOpen) return;
    loadNotificationsPreview({ showLoading: true });
  }, [notificationsOpen, loadNotificationsPreview]);

  const logout = async () => {
    try {
      await logoutUser();
    } catch {}

    localStorage.removeItem("user");
    localStorage.removeItem("tenant");
    navigate("/", { replace: true });
  };

  const toggleAccountMenu = () => {
    setAccountOpen((v) => !v);
    setNotificationsOpen(false);
    setSearchOpen(false);
  };

  const toggleNotifications = () => {
    setNotificationsOpen((v) => !v);
    setAccountOpen(false);
    setSearchOpen(false);
  };

  const resetSearchBox = () => {
    setSearchText("");
    setSearchSections([]);
    setSearchError("");
    setSearchLoading(false);
    setSearchOpen(false);
  };

  const openResult = (path) => {
    resetSearchBox();
    if (path) navigate(withFocusRefresh(path));
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const q = searchText.trim();
    resetSearchBox();
    navigate(q ? `/app/search?q=${encodeURIComponent(q)}` : "/app/search");
  };


  const openAllNotifications = () => {
    setNotificationsOpen(false);
    navigate("/app/notifications");
  };

  const openNotificationTarget = async (notification) => {
    const id = String(notification?._id || "").trim();
    if (id && !notification?.read) {
      setNotifications((current) => current.map((item) => String(item._id) === id ? { ...item, read: true } : item));
      setNotificationsUnread((current) => Math.max(0, current - 1));
      try {
        await setNotificationRead(id, true);
      } catch {}
    }
    setNotificationsOpen(false);
    if (notification?.targetPath) navigate(notification.targetPath);
  };

  return (
    <header className="topbar" onMouseDown={onAnyAction}>
      <div className="topbar_left">
        <div className="topbar_company">
          <div className={`topbar_company_logo ${companyLogoUrl ? "" : "vesta_default"}`}>
            {companyLogoUrl ? (
              <img src={companyLogoUrl} alt="Logo hotel" />
            ) : (
              <span className="topbar_vesta_mark" aria-hidden="true" />
            )}
          </div>

          <div className="topbar_company_text">
            <strong>{companyName || "VestaMNG"}</strong>
            <small>{companyCode || "DB non selezionato"}</small>
          </div>
        </div>
      </div>

      <div className="topbar_search_wrap" ref={searchWrapRef}>
        <form className="topbar_search" onSubmit={submitSearch}>
          <input
            type="search"
            value={searchText}
            onFocus={() => setSearchOpen(true)}
            onChange={(event) => { setSearchText(event.target.value); setSearchOpen(true); }}
            placeholder="Cerca in VestaMNG"
            aria-label="Cerca in VestaMNG"
          />
          <button type="submit">Cerca</button>
        </form>

        {searchOpen && searchText.trim().length >= 2 && (
          <div className="topbar_search_dropdown" role="listbox" aria-label="Risultati ricerca generale">
            <div className="topbar_search_dropdown_head">
              <strong>Risultati rapidi</strong>
              <span>{searchLoading ? "Ricerca..." : `${totalSearchResults} trovati`}</span>
            </div>

            {searchError && <div className="topbar_search_status is_error">{searchError}</div>}
            {!searchError && !searchLoading && totalSearchResults === 0 && <div className="topbar_search_status">Nessuna Corrispondenza Trovata</div>}

            {!searchError && searchSections.map((section) => (
              <div className="topbar_search_section" key={section.key}>
                <div className="topbar_search_section_title">{section.title}</div>
                {(section.items || []).map((item) => (
                  <button key={`${section.key}-${item.id}`} type="button" className="topbar_search_result" onClick={() => openResult(item.path || section.path)}>
                    <strong>{item.title}</strong>
                    <span>{item.subtitle || section.title}</span>
                    {item.meta && <em>{item.meta}</em>}
                  </button>
                ))}
              </div>
            ))}

            <button type="button" className="topbar_search_all" onClick={submitSearch}>Apri ricerca completa</button>
          </div>
        )}
      </div>

      <div className="topbar_right">
        <div className="topbar_action_wrap" ref={notificationsMenuRef}>
          <button
            type="button"
            className="topbar_icon_button topbar_notifications_button"
            onClick={toggleNotifications}
            aria-label={notificationsUnread > 0 ? `Apri notifiche, ${notificationsUnread} da leggere` : "Apri notifiche"}
          >
            <span className="topbar_bell" aria-hidden="true">🔔</span>
            {notificationsUnread > 0 && <span className="topbar_notifications_badge" aria-hidden="true">{notificationsUnread > 99 ? "99+" : notificationsUnread}</span>}
          </button>

          {notificationsOpen && (
            <div className="topbar_dropdown topbar_notifications_menu">
              <div className="topbar_dropdown_title">Notifiche</div>

              {notificationsLoading && <div className="topbar_empty_notifications">Caricamento notifiche...</div>}
              {!notificationsLoading && notificationsError && <div className="topbar_empty_notifications is_error">{notificationsError}</div>}
              {!notificationsLoading && !notificationsError && notifications.length === 0 && <div className="topbar_empty_notifications">Nessuna Notifica Presente</div>}

              {!notificationsLoading && !notificationsError && notifications.length > 0 && (
                <div className="topbar_notifications_list">
                  {notifications.map((notification) => (
                    <button
                      key={notification._id}
                      type="button"
                      className={`topbar_notification_item ${notification.read ? "is_read" : "is_unread"}`}
                      onClick={() => openNotificationTarget(notification)}
                    >
                      <span className={`topbar_notification_level level_${String(notification.level || "info").toLowerCase()}`} />
                      <strong>{notification.title}</strong>
                      {notification.message && <em>{notification.message}</em>}
                      <small>{notificationTypeLabel(notification)}{notification.createdAt ? ` · ${formatNotificationDate(notification.createdAt)}` : ""}</small>
                    </button>
                  ))}
                </div>
              )}

              <button type="button" className="topbar_notifications_all" onClick={openAllNotifications}>Vedi tutte le notifiche</button>
            </div>
          )}
        </div>

        <div className="topbar_action_wrap" ref={accountMenuRef}>
          <button
            type="button"
            className="topbar_icon_button topbar_user_button"
            onClick={toggleAccountMenu}
            aria-label="Apri menu utente"
          >
            <span className="topbar_user_icon" aria-hidden="true" />
          </button>

          {accountOpen && (
            <div className="topbar_dropdown topbar_account_menu">
              <div className="topbar_account_header">
                <strong>{accountName}</strong>
                <span>{accountRole}</span>
              </div>
              <button type="button">Profilo</button>
              <button type="button">Impostazioni</button>
              <button type="button" onClick={logout}>Logout</button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
